import { useEffect, useMemo, useState } from 'react';

import AddRoundedIcon from '@mui/icons-material/AddRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import PlaceRoundedIcon from '@mui/icons-material/PlaceRounded';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import StoreRoundedIcon from '@mui/icons-material/StoreRounded';
import {
  Alert,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import type { NormalizedApiError } from '@/shared/api';
import { ErrorState, LoadingState } from '@/shared/ui';

import {
  useAssignStoreLocationToRegion,
  useCreateStoreLocation,
  useCreateStoreRegion,
  useDeleteStoreLocation,
  useDeleteStoreRegion,
  useRemoveStoreLocationFromRegion,
  useStoreLocations,
  useStoreRegions,
  useStoreRegionsByLocation,
  useStoreServiceSettings,
  useToggleStoreLocation,
  useToggleStoreRegion,
  useUpdateStoreLocation,
  useUpdateStoreRegion,
  useUpdateStoreServiceSettings,
} from '../../hooks';
import { useDomainPermissions, useFeedback, useSettingsForm } from '../../lib';
import type {
  CreateStoreLocationRequest,
  CreateStoreRegionRequest,
  StoreLocationResponse,
  StoreRegionResponse,
  StoreRegionType,
  StoreServiceSettingsResponse,
  UpdateStoreLocationRequest,
  UpdateStoreRegionRequest,
} from '../../model/settingsTypes';
import {
  FeedbackSnackbar,
  FieldRow,
  LocationDialog,
  SelectField,
  SettingsCard,
  SettingsScaffold,
  SwitchField,
  TextInputField,
} from '../components';
import { STORE_REGION_TYPE_OPTIONS, STORE_SERVICE_LOCATION_TYPE_OPTIONS } from '../options';

type StoreTab = 'locations' | 'regions' | 'settings';

const REGION_EMPTY: CreateStoreRegionRequest = {
  code: '',
  name: '',
  type: 'CITY',
  active: true,
  defaultRegion: false,
};

function ActiveChip({ active }: { active: boolean }) {
  const { t } = useTranslation('settings');
  return (
    <Chip
      size="small"
      label={active ? t('storeProfileSettings.activeChip.active') : t('storeProfileSettings.activeChip.inactive')}
      color={active ? 'success' : 'default'}
      variant={active ? 'filled' : 'outlined'}
    />
  );
}

function RegionDialog({
  open,
  value,
  onClose,
  onSubmit,
  disabled,
}: {
  open: boolean;
  value: StoreRegionResponse | null;
  onClose: () => void;
  onSubmit: (body: CreateStoreRegionRequest | UpdateStoreRegionRequest) => void;
  disabled: boolean;
}) {
  const { t } = useTranslation('settings');
  const [draft, setDraft] = useState<CreateStoreRegionRequest>(REGION_EMPTY);

  useEffect(() => {
    if (open) {
      setDraft(
        value
          ? {
              code: value.code,
              name: value.name,
              type: value.type,
              active: value.active,
              defaultRegion: value.defaultRegion,
            }
          : REGION_EMPTY,
      );
    }
  }, [open, value]);

  const set = <K extends keyof CreateStoreRegionRequest>(key: K, next: CreateStoreRegionRequest[K]) =>
    setDraft((current) => ({ ...current, [key]: next }));

  const submit = () => {
    const common = { name: draft.name.trim(), type: draft.type, defaultRegion: draft.defaultRegion ?? false };
    onSubmit(value ? common : { ...common, code: draft.code.trim(), active: draft.active ?? true });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {value ? t('storeProfileSettings.regionDialog.editTitle') : t('storeProfileSettings.regionDialog.createTitle')}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <TextField
            label={t('storeProfileSettings.regionDialog.codeLabel')}
            value={draft.code}
            disabled={disabled || Boolean(value)}
            onChange={(event) => set('code', event.target.value)}
            required
            size="small"
          />
          <TextField
            label={t('storeProfileSettings.regionDialog.nameLabel')}
            value={draft.name}
            disabled={disabled}
            onChange={(event) => set('name', event.target.value)}
            required
            size="small"
          />
          <TextField
            select
            label={t('storeProfileSettings.regionDialog.typeLabel')}
            value={draft.type}
            disabled={disabled}
            onChange={(event) => set('type', event.target.value as StoreRegionType)}
            size="small"
          >
            {STORE_REGION_TYPE_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {t(option.label)}
              </MenuItem>
            ))}
          </TextField>
          <FormControlLabel
            control={
              <Switch
                checked={draft.defaultRegion ?? false}
                disabled={disabled}
                onChange={(event) => set('defaultRegion', event.target.checked)}
              />
            }
            label={t('storeProfileSettings.regionDialog.defaultRegionLabel')}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common:action.cancel')}</Button>
        <Button variant="contained" onClick={submit} disabled={disabled || !draft.code.trim() || !draft.name.trim()}>
          {t('common:action.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function LocationsTab({
  canManage,
  notify,
  notifyError,
  notifyWarning,
}: {
  canManage: boolean;
  notify: (message: string) => void;
  notifyError: (message: string) => void;
  notifyWarning: (message: string) => void;
}) {
  const { t } = useTranslation('settings');
  const [query, setQuery] = useState('');
  const [editing, setEditing] = useState<StoreLocationResponse | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const params = useMemo(() => ({ query: query.trim() || undefined, page: 0, size: 100 }), [query]);
  const locations = useStoreLocations(params);
  const create = useCreateStoreLocation();
  const update = useUpdateStoreLocation();
  const remove = useDeleteStoreLocation();
  const toggle = useToggleStoreLocation();
  // Reconciliation source for `save` below: the point's region binding(s) as of dialog-open
  // time. Same query key `LocationDialog` seeds its own Region field from, so this shares that
  // cache entry (TanStack Query dedupes identical active queries) rather than double-fetching.
  const currentRegions = useStoreRegionsByLocation(editing?.id ?? null);
  const assignRegion = useAssignStoreLocationToRegion();
  const removeRegionAssignment = useRemoveStoreLocationFromRegion();
  const busy = create.isPending || update.isPending || remove.isPending || toggle.isPending;

  const save = async (body: CreateStoreLocationRequest | UpdateStoreLocationRequest, regionId: string | null) => {
    try {
      const saved = editing
        ? await update.mutateAsync({ id: editing.id, body: body as UpdateStoreLocationRequest })
        : await create.mutateAsync(body as CreateStoreLocationRequest);
      setDialogOpen(false);
      notify(
        editing
          ? t('storeProfileSettings.locationsTab.savedFeedback')
          : t('storeProfileSettings.locationsTab.createdFeedback'),
      );

      // Region reconciliation — deliberate simplification (documented in full on
      // `LocationDialog`'s Region-seeding effect): the dialog's Region field is single-select
      // even though the backend binding is many-to-many, so this reconciles down to "at most
      // one bound region" — assign the newly-selected one (skip if already bound) and remove
      // every other previously-bound region. `Promise.allSettled` so one binding call failing
      // never hides that the location itself saved, and never cancels the other binding call.
      const previousRegionIds = editing ? (currentRegions.data?.items ?? []).map((region) => region.id) : [];
      const toAssign = regionId && !previousRegionIds.includes(regionId) ? [regionId] : [];
      const toRemove = previousRegionIds.filter((id) => id !== regionId);

      if (toAssign.length > 0 || toRemove.length > 0) {
        const results = await Promise.allSettled([
          ...toAssign.map((id) => assignRegion.mutateAsync({ regionId: id, locationId: saved.id })),
          ...toRemove.map((id) => removeRegionAssignment.mutateAsync({ regionId: id, locationId: saved.id })),
        ]);
        if (results.some((result) => result.status === 'rejected')) {
          notifyWarning(t('storeProfileSettings.locationsTab.regionBindingWarning'));
        }
      }
    } catch (error) {
      notifyError((error as NormalizedApiError).message);
    }
  };

  return (
    <SettingsCard
      title={t('storeProfileSettings.locationsTab.title')}
      description={t('storeProfileSettings.locationsTab.description')}
      icon={<PlaceRoundedIcon />}
      action={
        canManage ? (
          <Button
            variant="contained"
            size="small"
            startIcon={<AddRoundedIcon />}
            onClick={() => (setEditing(null), setDialogOpen(true))}
          >
            {t('storeProfileSettings.locationsTab.createButton')}
          </Button>
        ) : undefined
      }
      divided={false}
    >
      <Stack spacing={2} sx={{ pt: 2 }}>
        <TextField
          size="small"
          label={t('storeProfileSettings.locationsTab.searchLabel')}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={t('storeProfileSettings.locationsTab.searchPlaceholder')}
        />
        {locations.isLoading ? <LoadingState label={t('storeProfileSettings.locationsTab.loading')} /> : null}
        {locations.isError ? (
          <ErrorState
            title={t('storeProfileSettings.locationsTab.loadErrorTitle')}
            message={locations.error.message}
            onRetry={() => void locations.refetch()}
          />
        ) : null}
        {locations.isSuccess ? (
          locations.data.items.length === 0 ? (
            <Alert severity="info" variant="outlined">{t('storeProfileSettings.locationsTab.empty')}</Alert>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('storeProfileSettings.locationsTab.table.location')}</TableCell>
                  <TableCell>{t('storeProfileSettings.locationsTab.table.type')}</TableCell>
                  <TableCell>{t('storeProfileSettings.locationsTab.table.city')}</TableCell>
                  <TableCell>{t('storeProfileSettings.locationsTab.table.status')}</TableCell>
                  <TableCell align="right">{t('storeProfileSettings.locationsTab.table.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {locations.data.items.map((location) => {
                  // Translated type label (AC7/AC-i18n): fall back to the raw enum value if
                  // it's ever ahead of this app's known option list — never render blank.
                  const typeOption = STORE_SERVICE_LOCATION_TYPE_OPTIONS.find(
                    (option) => option.value === location.type,
                  );
                  return (
                    <TableRow key={location.id} hover>
                      <TableCell>
                        <Typography variant="subtitle2">{location.name}</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                          {location.code}
                        </Typography>
                      </TableCell>
                      <TableCell>{typeOption ? t(typeOption.label) : location.type}</TableCell>
                      <TableCell>{location.city || '—'}</TableCell>
                      <TableCell><ActiveChip active={location.active} /></TableCell>
                      <TableCell align="right">
                        <Tooltip
                          title={
                            location.active
                              ? t('storeProfileSettings.locationsTab.deactivateTooltip')
                              : t('storeProfileSettings.locationsTab.activateTooltip')
                          }
                        >
                          <span>
                            <Switch
                              size="small"
                              checked={location.active}
                              disabled={!canManage || busy}
                              onChange={() =>
                                toggle.mutate(
                                  { id: location.id, active: location.active },
                                  { onError: (error: NormalizedApiError) => notifyError(error.message) },
                                )
                              }
                            />
                          </span>
                        </Tooltip>
                        <IconButton
                          aria-label={t('storeProfileSettings.locationsTab.editAria')}
                          disabled={!canManage || busy}
                          onClick={() => (setEditing(location), setDialogOpen(true))}
                        >
                          <EditRoundedIcon />
                        </IconButton>
                        <IconButton
                          aria-label={t('storeProfileSettings.locationsTab.deleteAria')}
                          color="error"
                          disabled={!canManage || busy}
                          onClick={() =>
                            remove.mutate(location.id, {
                              onSuccess: () => notify(t('storeProfileSettings.locationsTab.deletedFeedback')),
                              onError: (error: NormalizedApiError) => notifyError(error.message),
                            })
                          }
                        >
                          <DeleteOutlineRoundedIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )
        ) : null}
      </Stack>
      <LocationDialog
        open={dialogOpen}
        value={editing}
        onClose={() => setDialogOpen(false)}
        onSubmit={save}
        disabled={!canManage || busy}
      />
    </SettingsCard>
  );
}

function RegionsTab({
  canManage,
  notify,
  notifyError,
}: {
  canManage: boolean;
  notify: (message: string) => void;
  notifyError: (message: string) => void;
}) {
  const { t } = useTranslation('settings');
  const [query, setQuery] = useState('');
  const [editing, setEditing] = useState<StoreRegionResponse | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const params = useMemo(() => ({ query: query.trim() || undefined, page: 0, size: 100 }), [query]);
  const regions = useStoreRegions(params);
  const create = useCreateStoreRegion();
  const update = useUpdateStoreRegion();
  const remove = useDeleteStoreRegion();
  const toggle = useToggleStoreRegion();
  const busy = create.isPending || update.isPending || remove.isPending || toggle.isPending;

  const save = (body: CreateStoreRegionRequest | UpdateStoreRegionRequest) => {
    if (editing) {
      update.mutate(
        { id: editing.id, body: body as UpdateStoreRegionRequest },
        {
          onSuccess: () => (setDialogOpen(false), notify(t('storeProfileSettings.regionsTab.savedFeedback'))),
          onError: (error: NormalizedApiError) => notifyError(error.message),
        },
      );
    } else {
      create.mutate(body as CreateStoreRegionRequest, {
        onSuccess: () => (setDialogOpen(false), notify(t('storeProfileSettings.regionsTab.createdFeedback'))),
        onError: (error: NormalizedApiError) => notifyError(error.message),
      });
    }
  };

  return (
    <SettingsCard
      title={t('storeProfileSettings.regionsTab.title')}
      description={t('storeProfileSettings.regionsTab.description')}
      icon={<StoreRoundedIcon />}
      action={
        canManage ? (
          <Button
            variant="contained"
            size="small"
            startIcon={<AddRoundedIcon />}
            onClick={() => (setEditing(null), setDialogOpen(true))}
          >
            {t('storeProfileSettings.regionsTab.createButton')}
          </Button>
        ) : undefined
      }
      divided={false}
    >
      <Stack spacing={2} sx={{ pt: 2 }}>
        <TextField
          size="small"
          label={t('storeProfileSettings.regionsTab.searchLabel')}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
        />
        {regions.isLoading ? <LoadingState label={t('storeProfileSettings.regionsTab.loading')} /> : null}
        {regions.isError ? (
          <ErrorState
            title={t('storeProfileSettings.regionsTab.loadErrorTitle')}
            message={regions.error.message}
            onRetry={() => void regions.refetch()}
          />
        ) : null}
        {regions.isSuccess ? (
          regions.data.items.length === 0 ? (
            <Alert severity="info" variant="outlined">{t('storeProfileSettings.regionsTab.empty')}</Alert>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('storeProfileSettings.regionsTab.table.region')}</TableCell>
                  <TableCell>{t('storeProfileSettings.regionsTab.table.type')}</TableCell>
                  <TableCell>{t('storeProfileSettings.regionsTab.table.default')}</TableCell>
                  <TableCell>{t('storeProfileSettings.regionsTab.table.status')}</TableCell>
                  <TableCell align="right">{t('storeProfileSettings.regionsTab.table.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {regions.data.items.map((region) => {
                  // Translated type label (AC7/AC-i18n): fall back to the raw enum value if
                  // it's ever ahead of this app's known option list — never render blank.
                  const typeOption = STORE_REGION_TYPE_OPTIONS.find((option) => option.value === region.type);
                  return (
                    <TableRow key={region.id} hover>
                      <TableCell>
                        <Typography variant="subtitle2">{region.name}</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                          {region.code}
                        </Typography>
                      </TableCell>
                      <TableCell>{typeOption ? t(typeOption.label) : region.type}</TableCell>
                      <TableCell>
                        {region.defaultRegion
                          ? t('storeProfileSettings.regionsTab.yes')
                          : t('storeProfileSettings.regionsTab.no')}
                      </TableCell>
                      <TableCell><ActiveChip active={region.active} /></TableCell>
                      <TableCell align="right">
                        <Switch
                          size="small"
                          checked={region.active}
                          disabled={!canManage || busy}
                          onChange={() =>
                            toggle.mutate(
                              { id: region.id, active: region.active },
                              { onError: (error: NormalizedApiError) => notifyError(error.message) },
                            )
                          }
                        />
                        <IconButton
                          aria-label={t('storeProfileSettings.regionsTab.editAria')}
                          disabled={!canManage || busy}
                          onClick={() => (setEditing(region), setDialogOpen(true))}
                        >
                          <EditRoundedIcon />
                        </IconButton>
                        <IconButton
                          aria-label={t('storeProfileSettings.regionsTab.deleteAria')}
                          color="error"
                          disabled={!canManage || busy}
                          onClick={() =>
                            remove.mutate(region.id, {
                              onSuccess: () => notify(t('storeProfileSettings.regionsTab.deletedFeedback')),
                              onError: (error: NormalizedApiError) => notifyError(error.message),
                            })
                          }
                        >
                          <DeleteOutlineRoundedIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )
        ) : null}
      </Stack>
      <RegionDialog open={dialogOpen} value={editing} onClose={() => setDialogOpen(false)} onSubmit={save} disabled={!canManage || busy} />
    </SettingsCard>
  );
}

function StoreServiceSettingsTab({
  settings,
  locations,
  canManage,
  onSaved,
  onError,
}: {
  settings: StoreServiceSettingsResponse;
  locations: StoreLocationResponse[];
  canManage: boolean;
  onSaved: (message: string) => void;
  onError: (message: string) => void;
}) {
  const { t } = useTranslation('settings');
  const form = useSettingsForm(settings);
  const update = useUpdateStoreServiceSettings();
  const draft = form.draft;
  if (!draft) return null;
  const locationOptions = locations.map((location) => ({
    value: location.id,
    label: t('storeProfileSettings.assignmentsTab.optionLabel', { name: location.name, code: location.code }),
  }));
  const disabled = !canManage || update.isPending;

  const save = () => {
    update.mutate(
      {
        expectedVersion: draft.version,
        defaultPickupEnabled: draft.defaultPickupEnabled,
        defaultLocationId: draft.defaultLocationId ?? null,
        pickupInstructions: draft.pickupInstructions ?? null,
        showLocationsOnStorefront: draft.showLocationsOnStorefront,
      },
      {
        onSuccess: () => onSaved(t('storeProfileSettings.storeServiceSettingsTab.savedFeedback')),
        onError: (error: NormalizedApiError) => onError(error.message),
      },
    );
  };

  return (
    <SettingsCard
      title={t('storeProfileSettings.storeServiceSettingsTab.title')}
      description={t('storeProfileSettings.storeServiceSettingsTab.description')}
      icon={<SettingsRoundedIcon />}
      action={
        <Button variant="contained" disabled={!form.dirty || disabled} onClick={save}>
          {t('storeProfileSettings.storeServiceSettingsTab.saveButton')}
        </Button>
      }
    >
      <SwitchField
        label={t('storeProfileSettings.storeServiceSettingsTab.defaultPickupLabel')}
        description={t('storeProfileSettings.storeServiceSettingsTab.defaultPickupDescription')}
        checked={draft.defaultPickupEnabled}
        onChange={(value) => form.setField('defaultPickupEnabled', value)}
        disabled={disabled}
      />
      <SwitchField
        label={t('storeProfileSettings.storeServiceSettingsTab.showLocationsLabel')}
        description={t('storeProfileSettings.storeServiceSettingsTab.showLocationsDescription')}
        checked={draft.showLocationsOnStorefront}
        onChange={(value) => form.setField('showLocationsOnStorefront', value)}
        disabled={disabled}
      />
      <SelectField
        label={t('storeProfileSettings.storeServiceSettingsTab.defaultLocationLabel')}
        description={t('storeProfileSettings.storeServiceSettingsTab.defaultLocationDescription')}
        value={draft.defaultLocationId ?? ''}
        options={locationOptions}
        onChange={(value) => form.setField('defaultLocationId', value || null)}
        disabled={disabled}
        allowEmpty
        emptyLabel={t('storeProfileSettings.storeServiceSettingsTab.defaultLocationEmpty')}
      />
      <TextInputField
        label={t('storeProfileSettings.storeServiceSettingsTab.pickupInstructionsLabel')}
        description={t('storeProfileSettings.storeServiceSettingsTab.pickupInstructionsDescription')}
        value={draft.pickupInstructions ?? ''}
        onChange={(value) => form.setField('pickupInstructions', value || null)}
        disabled={disabled}
        multiline
        rows={3}
      />
      <FieldRow
        label={t('storeProfileSettings.storeServiceSettingsTab.versionLabel')}
        description={t('storeProfileSettings.storeServiceSettingsTab.versionDescription')}
      >
        <Chip
          size="small"
          variant="outlined"
          label={t('storeProfileSettings.storeServiceSettingsTab.versionValue', { version: draft.version })}
        />
      </FieldRow>
    </SettingsCard>
  );
}

/** Store domain management: locations (incl. region binding + address autocomplete), regions and store-service settings. */
export function StoreProfileSettingsPage() {
  const { t } = useTranslation('settings');
  const { canView, canManage } = useDomainPermissions('store');
  const [tab, setTab] = useState<StoreTab>('locations');
  const settings = useStoreServiceSettings();
  const allLocations = useStoreLocations({ page: 0, size: 100 });
  const { feedback, notify, success, error: notifyError, clear } = useFeedback();

  const notifySuccess = (message: string) => success(message);
  const notifyWarning = (message: string) => notify('warning', message);
  const audit = settings.data
    ? { version: settings.data.version, updatedAt: settings.data.updatedAt, updatedBy: null }
    : null;

  return (
    <SettingsScaffold
      title={t('storeProfileSettings.title')}
      subtitle={t('storeProfileSettings.subtitle')}
      icon={<StoreRoundedIcon />}
      query={settings}
      canView={canView}
      canManage={canManage}
      audit={audit}
      feedback={<FeedbackSnackbar feedback={feedback} onClose={clear} />}
    >
      {(data) => {
        const locations = allLocations.data?.items ?? [];
        return (
          <Stack spacing={2.5}>
            {!canManage ? (
              <Alert severity="info" variant="outlined">
                {t('storeProfileSettings.readOnlyNotice')}
              </Alert>
            ) : null}
            <Tabs value={tab} onChange={(_event, value: StoreTab) => setTab(value)} variant="scrollable">
              <Tab value="locations" label={t('storeProfileSettings.tabs.locations')} />
              <Tab value="regions" label={t('storeProfileSettings.tabs.regions')} />
              <Tab value="settings" label={t('storeProfileSettings.tabs.settings')} />
            </Tabs>
            {tab === 'locations' ? (
              <LocationsTab
                canManage={canManage}
                notify={notifySuccess}
                notifyError={notifyError}
                notifyWarning={notifyWarning}
              />
            ) : null}
            {tab === 'regions' ? (
              <RegionsTab canManage={canManage} notify={notifySuccess} notifyError={notifyError} />
            ) : null}
            {tab === 'settings' ? (
              <StoreServiceSettingsTab
                settings={data}
                locations={locations}
                canManage={canManage}
                onSaved={notifySuccess}
                onError={notifyError}
              />
            ) : null}
            {allLocations.isError ? (
              <Alert severity="warning" variant="outlined">
                {allLocations.error.message}
              </Alert>
            ) : null}
          </Stack>
        );
      }}
    </SettingsScaffold>
  );
}
